import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton.jsx";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice.js";
import { appSettings } from "../../app/appSettingsSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants.js";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageVoucher = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [originalVoucherCount, setOriginalVoucherCount] = useState(0);
  const pageKey = "vouchers";
  const defaultVisibleColumns = {
    Voucher_Name: true,
    Voucher_DiscountPercentage: true,
    Voucher_Code: true,
    Voucher_Active: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Voucher_ID", label: "Voucher ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();

  

  const sortByOptions = [
    { value: "Voucher_ID", label: "Voucher ID" },
    { value: "Voucher_Name", label: "Voucher Name" },
    { value: "Voucher_DiscountPercentage", label: "Discount Percentage" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_VOUCHERS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      const voucherData = response.data.data || response.data;

      if (Array.isArray(voucherData)) {
        // Normalize keys to the UI's expected PascalCase with underscores
        const normalized = voucherData.map((v) => ({
          Voucher_ID: v?.Voucher_ID ?? v?.voucher_ID ?? v?.voucherId ?? v?.id ?? 0,
          Voucher_Name: v?.Voucher_Name ?? v?.voucher_Name ?? v?.voucherName ?? "",
          Voucher_DiscountPercentage:
            v?.Voucher_DiscountPercentage ?? v?.voucher_DiscountPercentage ?? v?.discountPercentage ?? 0,
          Voucher_Code: v?.Voucher_Code ?? v?.voucher_Code ?? v?.voucherCode ?? "",
          Voucher_Active: v?.Voucher_Active ?? v?.voucher_Active ?? v?.active ?? false,
        }));
        setVouchers(normalized);
        if (!search.trim()) {
          setOriginalVoucherCount(normalized.length);
        }
      } else {
        setVouchers([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to load vouchers.");
    }
  }, [search, sortBy, sortOrder, CompID]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchVouchers();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchVouchers]);

  const handleAddVoucher = () => navigate("/voucher/addvoucher");

  const handleRowClick = (voucherId) => {
    navigate(`/voucher/updateVoucher/${voucherId}`);
  };

  const handleDeleteVoucher = async (voucherItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete voucher ${voucherItem.Voucher_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!voucherItem.Voucher_ID || !CompID) {
        showErrorToast("Invalid voucher or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_VOUCHER, {
        params: {
          CompID,
          id: voucherItem.Voucher_ID,
        },
      });

      if (response.data.status) {
        setVouchers((prevVouchers) =>
          prevVouchers.filter((item) => item.Voucher_ID !== voucherItem.Voucher_ID)
        );
        showSuccessToast("Voucher deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the voucher.");
      setLoading(false);
    }
  };

  const handleSelectItem = (voucherId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (vouchers[i]?.Voucher_ID) {
            newSelected.add(vouchers[i].Voucher_ID);
          }
        }
      } else {
        if (newSelected.has(voucherId)) {
          newSelected.delete(voucherId);
        } else {
          newSelected.add(voucherId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(vouchers.map((item) => item.Voucher_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No vouchers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected vouchers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((voucherId) =>
          axiosInstance.delete(APIs.DELETE_VOUCHER, {
            params: {
              CompID,
              id: voucherId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchVouchers();
      showSuccessToast(`${selectedItems.size} vouchers deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected vouchers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No vouchers selected for editing.");
      return;
    }

    const selectedVoucherIds = Array.from(selectedItems);
    navigate(`/voucher/bulkEdit`, { state: { selectedVoucherIds } });
  };

  const renderNoDataMessage = () => {
    if (search.trim() && vouchers.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No vouchers found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Vouchers</li>
          </ol>
          <h1 className="page-header mb-0">Manage Vouchers</h1>
        </div>

        <div className="ms-auto">
          {!isChildBranch && (
            <Link to="/voucher/addvoucher">
              <button
                className="btn px-4"
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Create Voucher
              </button>
            </Link>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by voucher name, code..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Add Voucher Button - Below Filter Bar */}
                {!isChildBranch && !loading && originalVoucherCount === 0 && (
                  <div className="mb-4">
                    <AddButton
                      onClick={handleAddVoucher}
                      title="Add Voucher"
                      fontSize="16px"
                    />
                  </div>
                )}

                {/* Selection Info and Actions (Shown only when vouchers are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} voucher{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        {!isChildBranch && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={handleBulkEdit}
                            title="Edit Selected Vouchers"
                          >
                            <i className="fa fa-edit me-1"></i>
                            Edit
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Vouchers"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {renderNoDataMessage()}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    {vouchers.length > 0 && (
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                vouchers.length > 0 &&
                                selectedItems.size === vouchers.length
                              }
                            />
                          </th>
                          {visibleColumns.Voucher_Name && (
                            <th className="pt-2 pb-2 text-center">Name</th>
                          )}
                          {visibleColumns.Voucher_DiscountPercentage && (
                            <th className="pt-2 pb-2 text-center">Discount Percentage</th>
                          )}
                          {visibleColumns.Voucher_Code && (
                            <th className="pt-2 pb-2 text-center">Code</th>
                          )}
                          {visibleColumns.Voucher_Active && (
                            <th className="pt-2 pb-2 text-center">Active</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {vouchers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            {search.trim() ? "No vouchers found matching your search criteria." : "No vouchers available."}
                          </td>
                        </tr>
                      ) : (
                        vouchers.map((voucher, index) => (
                        <tr
                          key={voucher.Voucher_ID}
                          onClick={() => {
                            if (isChildBranch) return;
                            handleRowClick(voucher.Voucher_ID);
                          }}
                          style={{ cursor: isChildBranch ? "default" : "pointer" }}
                        >
                          <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedItems.has(voucher.Voucher_ID)}
                              onChange={(e) =>
                                handleSelectItem(voucher.Voucher_ID, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          {visibleColumns.Voucher_Name && (
                            <td className="align-middle text-center">
                              {voucher.Voucher_Name}
                            </td>
                          )}
                          {visibleColumns.Voucher_DiscountPercentage && (
                            <td className="align-middle text-center">
                              {voucher.Voucher_DiscountPercentage}%
                            </td>
                          )}
                          {visibleColumns.Voucher_Code && (
                            <td className="align-middle text-center">
                              {voucher.Voucher_Code}
                            </td>
                          )}
                          {visibleColumns.Voucher_Active && (
                            <td className="align-middle text-center">
                              {voucher.Voucher_Active ? "Yes" : "No"}
                            </td>
                          )}
                          <td className="align-middle text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex justify-content-center gap-2">
                              {/* Edit Icon */}
                              {!isChildBranch && (
                                <Link
                                  to={`/voucher/updateVoucher/${voucher.Voucher_ID}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Voucher"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </Link>
                              )}

                              {/* Delete Icon */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteVoucher(voucher);
                                }}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Voucher"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ManageVoucher;